<template>
  <div class="entity-search-single">
    <!-- Trigger Button -->
    <button
      @click="openModal"
      :class="buttonClass"
      class="btn-primary"
    >
      <FontAwesomeIcon :icon="['fas', 'search']" />
      {{ buttonText }}
    </button>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="cancelSelection"></div>

      <!-- Modal Content -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
          <!-- Modal Header -->
          <div class="bg-primary-100 text-gray-900 px-6 py-4">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon :icon="['fas', 'search']" />
                {{ modalTitle }}
              </h2>
              <button @click="cancelSelection" class="text-gray-600 hover:text-gray-900 transition-colors">
                <FontAwesomeIcon :icon="['fas', 'times']" class="text-xl" />
              </button>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
            <!-- Error Message -->
            <div v-if="errorMessage" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-3">
                <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="text-red-500 mt-0.5" />
                <p class="text-sm text-red-700">Internal error: {{ errorMessage }}</p>
              </div>
            </div>

            <!-- Search Controls -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <!-- Entity Type Filter -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Entity Type</label>
                <select
                  v-model="filters.entityType"
                  @change="searchEntities"
                  :disabled="isFilteredByType"
                  class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option :value="null">Select an entity type...</option>
                  <option v-for="type in entityTypes" :key="type.id" :value="type.id">
                    {{ type.type_name }}
                  </option>
                </select>
              </div>

              <!-- Name Search -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Search by Name</label>
                <div class="flex items-center bg-white border border-gray-200 rounded-lg px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                  <FontAwesomeIcon :icon="['fas', 'search']" class="text-gray-400 mr-2" />
                  <input
                    v-model="searchText" data-demo-search
                    @input="debouncedSearch"
                    placeholder="Enter entity name..."
                    class="sensitive flex-1 py-2 outline-none text-sm"
                  />
                  <button
                    v-if="searchText"
                    @click="clearSearch"
                    class="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <FontAwesomeIcon :icon="['fas', 'times']" class="text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Results Stats -->
            <div class="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg">
              <span class="text-sm text-gray-600">
                <span v-if="loading">Searching...</span>
                <span v-else-if="!filters.entityType">Select an entity type to search</span>
                <span v-else>{{ totalResults }} entities found</span>
              </span>
              <span v-if="multiple && selectedEntities.length > 0" class="text-sm font-medium text-blue-600">
                {{ selectedEntities.length }} selected
              </span>
            </div>

            <!-- Results Grid -->
            <div class="border border-gray-200 rounded-lg overflow-hidden">
              <div v-if="loading" class="flex justify-center items-center py-12">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>

              <div v-else-if="!loading && results.length === 0" class="text-center py-12">
                <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-4xl text-gray-300 mb-3" />
                <p class="text-gray-500">
                  {{ filters.entityType ? 'No entities found' : 'Please select an entity type to begin' }}
                </p>
              </div>

              <ag-grid-vue
                v-else
                :style="{ height: '400px', width: '100%' }"
                class="ag-theme-alpine"
                :column-defs="columnDefs"
                :row-data="results"
                :default-col-def="defaultColDef"
                :row-selection="rowSelection"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
                @row-double-clicked="onRowDoubleClicked"
              />
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div class="flex justify-between items-center">
              <div class="text-sm text-gray-600">
                <span v-if="selectedEntities.length === 0">No {{ multiple ? 'entities' : 'entity' }} selected</span>
                <span v-else-if="selectedEntities.length === 1">Selected: <span class="sensitive">{{ selectedEntities[0].name }}</span></span>
                <span v-else>{{ selectedEntities.length }} entities selected</span>
              </div>
              <div class="flex gap-3">
                <button
                  @click="cancelSelection"
                  class="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  @click="confirmSelection"
                  :disabled="selectedEntities.length === 0"
                  class="btn-primary"
                >
                  <FontAwesomeIcon :icon="['fas', 'check']" />
                  <span v-if="multiple && selectedEntities.length > 0">Select ({{ selectedEntities.length }})</span>
                  <span v-else>Select</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { sensitiveSetFilterParams } from '@/utils/demoSpoof';
import demoProtectedFields from '@/mixins/demoProtectedFields';

export default {
  name: 'EntitySearchSingle',

  components: {
    AgGridVue,
    FontAwesomeIcon,
  },
  mixins: [demoProtectedFields],
  // The name search runs over practice / provider / facility names, which are
  // tagged. Demo Mode empties it and the tagged input is read-only; the entity
  // type filter stays usable. Not persisted.
  demoProtectedFields: ['searchText'],

  props: {
    buttonText: {
      type: String,
      default: 'Select Entity'
    },
    modalTitle: {
      type: String,
      default: 'Select Entity'
    },
    buttonClass: {
      type: String,
      default: ''
    },
    filterEntityTypeName: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    excludeIds: {
      type: Array,
      default: () => []
    }
  },

  emits: ['entity-selected', 'entities-selected'],

  data() {
    return {
      // Modal control
      showModal: false,
      errorMessage: '',

      // Search
      searchText: '',

      // Filters
      filters: {
        entityType: null
      },

      // Entity types
      entityTypes: [],

      // Results
      results: [],
      totalResults: 0,
      loading: false,

      // Selection
      selectedEntities: [],

      // AG Grid
      gridApi: null,
      columnDefs: [
        {
          field: 'name',
          headerName: 'Name',
          flex: 2,
          minWidth: 200,
          // Entity names are practice / provider / facility identities.
          cellClass: 'sensitive',
          filterParams: { ...sensitiveSetFilterParams },
        },
        {
          field: 'entity_type_name',
          headerName: 'Type',
          flex: 1,
          minWidth: 120
        }
      ],
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      }
    };
  },

  computed: {
    isFilteredByType() {
      return !!this.filterEntityTypeName;
    },
    rowSelection() {
      return {
        mode: this.multiple ? 'multiRow' : 'singleRow',
        checkboxes: true,
        headerCheckbox: this.multiple
      };
    }
  },

  created() {
    this.debouncedSearch = debounce(this.searchEntities, 300);
  },

  methods: {
    openModal() {
      this.showModal = true;
      this.loadEntityTypes();
      // Don't load entities until an entity type is selected
    },

    async loadEntityTypes() {
      try {
        const response = await axios.get(`api/entity-types/`);
        this.entityTypes = response.data;
        this.errorMessage = '';

        if (this.filterEntityTypeName) {
          const matchingType = this.entityTypes.find(
            type => type.type_name === this.filterEntityTypeName
          );
          if (matchingType) {
            this.filters.entityType = matchingType.id;
            this.searchEntities();
          } else {
            this.errorMessage = 'The specified entity type could not be found.';
            console.error(`Entity type "${this.filterEntityTypeName}" not found in available entity types`);
          }
        }
      } catch (error) {
        this.errorMessage = 'Failed to load entity types.';
        console.error('Failed to load entity types:', error);
      }
    },

    async searchEntities() {
      // Don't search if no entity type is selected
      if (!this.filters.entityType) {
        this.results = [];
        this.totalResults = 0;
        return;
      }

      this.loading = true;

      try {
        const params = {
          entity_type_id: this.filters.entityType
        };

        if (this.searchText) {
          params.name = this.searchText;
        }

        const response = await axios.get(`api/entities/`, { params });

        let results = response.data.results || response.data;
        if (this.excludeIds.length > 0) {
          const excluded = new Set(this.excludeIds);
          results = results.filter(e => !excluded.has(e.id));
        }
        this.results = results;
        this.totalResults = this.results.length;

      } catch (error) {
        console.error('Search failed:', error);
        this.results = [];
        this.totalResults = 0;
      } finally {
        this.loading = false;
      }
    },

    clearSearch() {
      this.searchText = '';
      this.searchEntities();
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();
    },

    onSelectionChanged() {
      if (this.gridApi) {
        const selectedNodes = this.gridApi.getSelectedNodes();
        this.selectedEntities = selectedNodes.map(node => node.data);
      }
    },

    onRowDoubleClicked(event) {
      // Only allow double-click selection in single mode
      if (!this.multiple) {
        this.selectedEntities = [event.data];
        this.confirmSelection();
      }
    },

    confirmSelection() {
      if (this.selectedEntities.length > 0) {
        if (this.multiple) {
          this.$emit('entities-selected', this.selectedEntities);
        } else {
          this.$emit('entity-selected', this.selectedEntities[0]);
        }
        this.showModal = false;
        this.selectedEntities = [];
        // Reset filters and search (don't reset entity type if it's locked via prop)
        this.searchText = '';
        this.results = [];
        this.totalResults = 0;
        if (!this.filterEntityTypeName) {
          this.filters.entityType = null;
        }

        // Clear grid selection
        if (this.gridApi) {
          this.gridApi.deselectAll();
        }
      }
    },

    cancelSelection() {
      this.showModal = false;
      this.selectedEntities = [];
      // Reset filters and search (don't reset entity type if it's locked via prop)
      this.searchText = '';
      this.results = [];
      this.totalResults = 0;
      if (!this.filterEntityTypeName) {
        this.filters.entityType = null;
      }

      // Clear grid selection
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
    }
  }
};
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.2s ease;
}
.dropdown-enter, .dropdown-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
